import React, { useMemo, useState } from "react";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import { ShiftSummaryCategory } from "../../../pages/reviewAndPublish/model";
import { buildProcessPathData } from "../../../pages/shiftPlanning/CurrentPlanSummary/v2/processPathRows";

interface ShiftDataTableProps {
  /** Full shift summary — the "headcount" category plus the synthesized "hours"
   *  category (see processShiftSummaryData). Both render as expandable sections. */
  shiftSummary?: ShiftSummaryCategory[];
}

interface ProcessedRow {
  id: string;
  label: string;
  values: number[];
}

interface ProcessedSection {
  key: string;
  title: string;
  timeSlots: string[];
  rows: ProcessedRow[];
  totals: number[];
}

const numberFormatter = new Intl.NumberFormat("en-US");

const formatValue = (value: number): string =>
  Number.isInteger(value) ? numberFormatter.format(value) : numberFormatter.format(Number(value.toFixed(2)));

// Human-readable section titles for the known categories.
const SECTION_TITLES: Record<string, string> = {
  headcount: "Headcount Totals",
  hours: "Hours Totals",
};

/**
 * Refactored (V2) Process Path Analysis table — UTR-REFLEX-1165.
 *
 * Renders both the "headcount" and synthesized "hours" categories as expandable
 * grouped sections using Meridian's Table (native nested/expandable rows). Each
 * section's totals row is the parent; its process-path rows are nested children
 * that Meridian shows/hides via the `open` prop. Wrapped in an outline Box so the
 * surface + padding come from Meridian rather than custom CSS. Sibling of the V1
 * ShiftDataTable so V1 is untouched.
 */
const ShiftDataTable: React.FC<ShiftDataTableProps> = ({ shiftSummary }) => {
  // Which sections are expanded (default: all expanded).
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const sections: ProcessedSection[] = useMemo(() => {
    if (!shiftSummary || shiftSummary.length === 0) {
      return [];
    }

    return shiftSummary
      .filter((category) => category.planOutput && category.planOutput.length > 0)
      .map((category) => {
        // Shared derivation (labels, per-slot values with 0-fill, descending sort
        // by first slot, column totals) — kept identical to the PDF export.
        const { timeSlots, rows, totals } = buildProcessPathData(category);

        return {
          key: category.heading,
          title: SECTION_TITLES[category.heading] ??
            `${category.heading.charAt(0).toUpperCase()}${category.heading.slice(1)} Totals`,
          timeSlots,
          rows: rows.map((row) => ({
            id: `${category.heading}-${row.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`,
            label: row.label,
            values: row.values,
          })),
          totals,
        };
      });
  }, [shiftSummary]);

  if (sections.length === 0) {
    return null;
  }

  // All sections share the same time-slot columns; use the first section's.
  const timeSlots = sections[0].timeSlots;

  return (
    <Box type="outline" spacingInset="400">
      <Column spacing="300">
        <Heading type="h400" level={2}>
          Process Path Analysis
        </Heading>

        <Table
          headerRows={1}
          headerColumns={1}
          showDividers={true}
          fixHeaderRows={false}
          spacing="small"
        >
          {/* Header row: sticky "All" label + one column per time slot */}
          <TableRow>
            <TableCell header={true}>All</TableCell>
            {timeSlots.map((slot) => (
              <TableCell key={slot} header={true} alignmentHorizontal="end">
                {slot}
              </TableCell>
            ))}
          </TableRow>

          {sections.map((section) => {
            const isOpen = !collapsedSections.has(section.key);
            return (
              // Group/totals row is the parent; process-path rows are nested children
              // that Meridian shows/hides via `open` (native expand/collapse — no
              // custom caret or CSS animation needed).
              <TableRow
                key={section.key}
                open={isOpen}
                onClick={() => toggleSection(section.key)}
                highlightOnHover={true}
              >
                <TableCell header={true}>{section.title}</TableCell>
                {section.totals.map((total, i) => (
                  <TableCell key={i} alignmentHorizontal="end">
                    {formatValue(total)}
                  </TableCell>
                ))}

                {section.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell header={true}>
                      <Text type="b300">{row.label}</Text>
                    </TableCell>
                    {row.values.map((value, i) => (
                      <TableCell key={i} alignmentHorizontal="end">
                        {formatValue(value)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableRow>
            );
          })}
        </Table>
      </Column>
    </Box>
  );
};

export default ShiftDataTable;
